﻿import {translation} from "@2d-front-monorepo/core";
import {
	Message,
	MvvmButton,
	toggleLoadingDialog, RemoteDataStatus, ErrorMessage
} from "@2d-front-monorepo/design-library";

import {
	formatCurrency, ReservationId,
	ReservationQuote,
	Reservations,
	Tariffs,
	VoidReservation
} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";

import {JSX, useCallback} from "react";
import {useNavigate, useParams} from "react-router";
import InvalidReservation from "../../components/InvalidReservation";
import {PaymentMethodComponent, PaymentMethodComponentState, PaymentMethodHeader} from "../../components/PaymentMethodComponent";
import {PermitsBackButton} from "../../components/PermitsBackButton";
import PermitsInlineLoading from "../../components/PermitsInlineLoading";
import {loadReservationParkingTypeData} from "../../hooks/parkingTypeHelpers";
import {ReloadEntities, reloadPermits} from "../../hooks/reloadPermits";
import {getGlobalContext} from "../../hooks/getGlobalContext";
import {getHttpClient} from "../../hooks/getHttpClient";
import {usePermits} from "../../hooks/usePermits";
import {PrivateRoutes} from "../../routes";

function VoidReservationsMessage({voids}: { voids: VoidReservation[] }): JSX.Element {
	if (voids.length === 0) {
		return <></>;
	}

	const total = voids.reduce((previousValue, currentValue) => previousValue + currentValue.total, 0);

	return (
		<Message title={{text: PermitTranslations.reservation__void_payment__title}} type="error">
			{voids.map(x =>
				<p className="layout horizontal aligned middle" key={x.id}>
					<span className="flex">– {x.purpose}</span>
					<span className={"tabular-nums"}>{formatCurrency(x.total)}</span>
				</p>)}

			<div className="layout horizontal aligned middle">
				<strong className="flex">{translation(PermitTranslations.reservation__void_payment__total)}</strong>
				<strong className={"tabular-nums"}>{formatCurrency(total)}</strong>
			</div>
		</Message>);
}

export default function ApproveReservation(): JSX.Element {
	const {reservationId} = useParams<{ reservationId: string }>() as { reservationId: ReservationId };
	const {installation} = getGlobalContext();
	const permitsState = usePermits();
	const navigate = useNavigate();
	const reservation = Reservations.find(permitsState, reservationId);
	const voidReservations = permitsState.voidReservations;

	const {data, status, error} = loadReservationParkingTypeData(reservation?.parkingType);

	const onFormSubmit = useCallback(async function (data: PaymentMethodComponentState): Promise<void> {
		try {
			toggleLoadingDialog(true);

			await getHttpClient().reservations.approve(reservationId, data.selected);
			await reloadPermits(ReloadEntities.Reservations);
			navigate(PrivateRoutes.permitsList);

		} finally {
			toggleLoadingDialog(false);
		}
	}, [reservationId]);

	if (reservation == null) {
		return <InvalidReservation reservationId={reservationId}/>;
	}

	if (status === RemoteDataStatus.Loading) {
		return <PermitsInlineLoading/>;
	}

	if (status === RemoteDataStatus.Error) {
		return <ErrorMessage error={error}/>;
	}

	const quotation = reservation.payment?.quotation;
	if (quotation == null) {
		// Read-model lag or a deep link to a not-yet-quoted reservation: a defaulted total of 0
		// would trip PaymentMethodComponent's free-permit fast path and auto-approve without a click.
		return <PermitsInlineLoading/>;
	}

	return (<>
			<h1>{translation(PermitTranslations.reservation_page__approve, {tariff: Tariffs.get(installation, reservation.tariffId).title})}</h1>
			<hr/>
			<PaymentMethodComponent available={data?.enabledPaymentMethods ?? []} onSubmit={onFormSubmit} total={quotation.price.total}
			                        className={"layout vertical gap"}>

				<PaymentMethodHeader>
					<ReservationQuote reservation={reservation} installation={installation}/>
					<VoidReservationsMessage voids={voidReservations}/>
				</PaymentMethodHeader>

				<div className="form__buttons">
					<MvvmButton kind="primary">{translation(PermitTranslations.accept_and_pay)}</MvvmButton>
					<span></span>
					<PermitsBackButton/>
				</div>
			</PaymentMethodComponent>
		</>
	);
}
